/**
 * PresenceClient
 * SignalR client for PresenceHub (/presencehub)
 * Manages real-time user presence tracking and status updates
 */
import { BaseSignalRClient } from '../shared/BaseSignalRClient';
import { PresenceStatus } from './types';
/**
 * Presence hub client
 */
export class PresenceClient extends BaseSignalRClient {
    constructor(hubUrl, getToken, options = {}) {
        super(hubUrl, getToken, options);
        this.onlineUsers = new Map();
        this.currentUserPresence = null;
    }
    /**
     * Get current user's presence
     */
    getCurrentUserPresence() {
        return this.currentUserPresence;
    }
    /**
     * Get online users map
     */
    getOnlineUsersMap() {
        return new Map(this.onlineUsers);
    }
    /**
     * Update current user's status
     */
    async updateStatus(status, activityMessage) {
        // Additional safety check - ensure we're actually connected
        if (!this.isConnected()) {
            console.warn('[PresenceClient] Cannot update status - not connected');
            throw new Error('Cannot update status: not connected to presence hub');
        }
        // Convert status to numeric value for SignalR serialization (backend expects int enum)
        let numericStatus;
        if (typeof status === 'string') {
            // Handle string enum names (for backwards compatibility)
            const statusMap = {
                'Offline': PresenceStatus.Offline,
                'Online': PresenceStatus.Online,
                'Away': PresenceStatus.Away,
                'Busy': PresenceStatus.Busy,
            };
            numericStatus = statusMap[status];
            if (numericStatus === undefined) {
                throw new Error(`Invalid presence status: ${status}`);
            }
        }
        else {
            // Already a numeric enum value
            numericStatus = Number(status);
        }
        await this.invoke('UpdateStatus', numericStatus, activityMessage || null);
    }
    /**
     * Get list of online user IDs
     */
    async getOnlineUsers() {
        return await this.invoke('GetOnlineUsers');
    }
    /**
     * Get specific user's presence
     */
    async getUserPresence(userId) {
        return await this.invoke('GetUserPresence', userId);
    }
    /**
     * Get all presence records (online + recent offline)
     */
    async getAllPresence() {
        return await this.invoke('GetAllPresence');
    }
    /**
     * Send heartbeat to server (for activity tracking)
     */
    async heartbeat() {
        // Additional safety check - ensure we're actually connected
        if (!this.isConnected()) {
            console.warn('[PresenceClient] Cannot send heartbeat - not connected');
            throw new Error('Cannot send heartbeat: not connected to presence hub');
        }
        await this.invoke('Heartbeat');
    }
    /**
     * Record activity (automatically changes Away → Online if needed)
     */
    async recordActivity() {
        await this.invoke('RecordActivity');
    }
    // ===== ADMIN METHODS (Require Admin role in JWT) =====
    /**
     * Get presence statistics (Admin only)
     * Returns detailed statistics about user presence status distribution
     */
    async getPresenceStats() {
        return await this.invoke('GetPresenceStats');
    }
    /**
     * Set all users offline - maintenance mode (Admin only)
     * Sets all users to offline status, useful for maintenance or system reset
     */
    async setAllUsersOffline() {
        await this.invoke('SetAllUsersOffline');
    }
    /**
     * Force disconnect a specific user (Admin only)
     * Forcefully disconnects a user from the presence hub
     * @param userId - The user ID to disconnect
     */
    async forceDisconnectUser(userId) {
        await this.invoke('ForceDisconnectUser', userId);
    }
    /**
     * Setup server-side method handlers
     * Registers handlers for events sent from the server
     */
    setupServerMethods() {
        if (!this.connection)
            return;
        // Get current user ID from auth state (will be set after connection)
        const getCurrentUserId = () => {
            return this.currentUserPresence?.userId || null;
        };
        // User comes online (first connection)
        // Backend sends: PresenceUpdate object directly
        this.connection.on('UserIsOnline', (presence) => {
            const currentUserId = getCurrentUserId();
            // Check if this is the current user receiving their own status
            if (presence.userId === currentUserId || !currentUserId) {
                this.currentUserPresence = presence;
                this.emit('currentUserPresenceChanged', presence);
            }
            else {
                this.onlineUsers.set(presence.userId, presence);
                this.emit('userOnline', presence); // Emit presence directly, not wrapped
            }
        });
        // User goes offline (last connection closed)
        // Backend sends: PresenceUpdate object directly
        this.connection.on('UserIsOffline', (presence) => {
            this.onlineUsers.delete(presence.userId);
            this.emit('userOffline', presence); // Emit presence directly, not just userId
        });
        // User status changed (manual status update or activity change)
        // Backend sends: PresenceUpdate object directly
        this.connection.on('UserStatusChanged', (presence) => {
            const currentUserId = getCurrentUserId();
            // Check if this is the current user's status change
            if (presence.userId === currentUserId) {
                this.currentUserPresence = presence;
                this.emit('currentUserPresenceChanged', presence);
            }
            this.onlineUsers.set(presence.userId, presence);
            this.emit('statusChanged', presence); // Emit presence directly, not wrapped
        });
        // Admin force-disconnected this user
        this.connection.on('ForceDisconnected', (reason) => {
            console.warn('[PresenceClient] Force disconnected by admin:', reason);
            this.emit('forceDisconnected', reason);
            // Disconnect cleanly
            this.disconnect().catch(err => {
                console.error('[PresenceClient] Error during force disconnect cleanup:', err);
            });
        });
    }
    /**
     * Override connect to initialize presence data
     */
    async connect() {
        await super.connect();
        // Load initial presence data after connection
        try {
            const onlineUserIds = await this.getOnlineUsers();
            console.log('[PresenceClient] Loaded online users:', onlineUserIds.length);
            const allPresence = await this.getAllPresence();
            console.log('[PresenceClient] Loaded all presence:', allPresence.length);
            // Emit initial state for all online users
            allPresence.forEach(presence => {
                if (presence.isOnline) {
                    this.onlineUsers.set(presence.userId, presence);
                    this.emit('userOnline', presence);
                }
            });
        }
        catch (error) {
            console.error('[PresenceClient] Failed to load initial presence:', error);
            // Don't throw - allow connection to proceed
            // The server will send real-time updates as they happen
        }
    }
    /**
     * Override disconnect to clear local state
     */
    async disconnect() {
        this.onlineUsers.clear();
        this.currentUserPresence = null;
        await super.disconnect();
    }
}
//# sourceMappingURL=PresenceClient.js.map